/**
 * Utilidades para manejar metadata de documentos — soporta ambos shapes:
 *
 * - **Plain (legacy/list view):** `{ campo: "valor" }`
 * - **Rich (detail view, rework #19):** `{ campo: { value: "valor", source: "CSV_POSITIONAL", sourceUser: "jdoe", sourceAt: "...", confidence: null } }`
 *
 * Los componentes MetadataDisplay y MetadataEditor usan estos helpers para
 * funcionar con ambos shapes sin romper la retrocompatibilidad.
 *
 * Ver ADR-009 decisión #1 (opción B: metadata rich en wire JSON).
 */

/**
 * Extrae el valor string de una entrada de metadata, soportando ambos shapes.
 * @param {string|object} entry - Puede ser un string plain o un objeto {value, source, ...}
 * @returns {string|null} El valor como string, o null.
 */
export const extractValue = (entry) => {
  if (entry === null || entry === undefined) return null;
  if (typeof entry === 'object' && entry !== null && 'value' in entry) return entry.value;
  return entry; // Preservar el tipo original (string, number, boolean) para que formatMetadataValue lo maneje
};

/**
 * Extrae el source de una entrada de metadata rich. Devuelve null para plain entries.
 * @param {string|object} entry
 * @returns {string|null} El source ("CSV_POSITIONAL", "OCR_AI", "MANUAL", etc.) o null.
 */
export const extractSource = (entry) => {
  if (typeof entry === 'object' && entry !== null && 'source' in entry) return entry.source;
  return null;
};

/**
 * Extrae el sourceUser de una entrada de metadata rich.
 * @param {string|object} entry
 * @returns {string|null}
 */
export const extractSourceUser = (entry) => {
  if (typeof entry === 'object' && entry !== null && 'sourceUser' in entry) return entry.sourceUser;
  return null;
};

/**
 * Extrae el sourceAt de una entrada de metadata rich.
 * @param {string|object} entry
 * @returns {string|null}
 */
export const extractSourceAt = (entry) => {
  if (typeof entry === 'object' && entry !== null && 'sourceAt' in entry) return entry.sourceAt;
  return null;
};

/**
 * Extrae la confidence de una entrada de metadata rich.
 * @param {string|object} entry
 * @returns {number|null}
 */
export const extractConfidence = (entry) => {
  if (typeof entry === 'object' && entry !== null && 'confidence' in entry) return entry.confidence;
  return null;
};

/**
 * Determina si una entrada de metadata es rich (tiene trazabilidad) o plain.
 * @param {string|object} entry
 * @returns {boolean}
 */
export const isRichMetadata = (entry) => {
  return typeof entry === 'object' && entry !== null && 'value' in entry;
};

/**
 * Convierte un mapa de metadata (posiblemente mixto) a un mapa plain (solo valores string).
 * Útil cuando se necesita enviar metadata al server como Map<String, String>.
 * @param {Object} metadata - Mapa de metadata, plain o rich.
 * @returns {Object} Mapa con solo los valores string.
 */
export const toPlainMetadata = (metadata) => {
  if (!metadata) return {};
  const plain = {};
  for (const [key, entry] of Object.entries(metadata)) {
    plain[key] = extractValue(entry);
  }
  return plain;
};

/**
 * Labels legibles para cada source.
 */
export const SOURCE_LABELS = {
  CSV_POSITIONAL: 'CSV',
  OCR_AI: 'OCR',
  MANUAL: 'Manual',
  SYSTEM: 'Sistema',
  LEGACY: 'Legacy',
};

/**
 * Colores para badges de source.
 */
export const SOURCE_COLORS = {
  CSV_POSITIONAL: '#2196F3',
  OCR_AI: '#FF9800',
  MANUAL: '#4CAF50',
  SYSTEM: '#9E9E9E',
  LEGACY: '#795548',
};
